<template>
  <div class="page-wrapper">
      <div class="login-container">
        <div class="background-decoration">
            <div class="decoration-circle circle-1"></div>
            <div class="decoration-circle circle-2"></div>
            <div class="decoration-circle circle-3"></div>
        </div>

        <div class="header-section">
            <div class="header-content">
                <div class="header-top">
                    <img class="app-logo" src="@/assets/static/121212e.png">
                    <div class="header-actions">
                        <div class="action-btn kefu-btn" @click="handleKefu">
                            <div class="icon-wrapper"><span class="icon-text">💬</span></div>
                        </div>
                        <div class="action-btn lang-btn" @click="languageDropdownRef?.toggleDropdown()" style="background: rgba(255, 255, 255, 0.2);">
                            <div class="icon-wrapper"><span class="icon-text">🌐</span></div>
                        </div>
                    </div>
                </div>
                <LanguageDropdown ref="languageDropdownRef" />
            </div>
            <div class="hero-image"><img src="@/assets/static/swiper7.png" class="hero-img"></div>
            <div class="welcome-section"><span class="welcome-title">{{ t('login.title') }}</span><span class="welcome-subtitle">{{ t('login.subtitle2') }}</span></div>
        </div>
     


         <div class="login-form-section">
            <div class="form-card">
                <div class="form-header"><span class="form-title">{{ t('login.welcome') }}</span><span class="form-subtitle">{{ t('login.subtitle') }}</span></div>
                <div class="form-content">
                    <div class="input-group">
                        <div class="input-container">
                            <div class="input-icon"><span class="icon">👤</span></div>
                            <input type="text" :placeholder="t('login.phonePlaceholder')" class="form-input" v-model="formData.tel">
                        </div>
                    </div>
                    <div class="input-group">
                        <div class="input-container">
                            <div class="input-icon"><span class="icon">🔒</span></div>
                            <input :type="showPassword ? 'text' : 'password'" :placeholder="t('login.passwordPlaceholder')" class="form-input" v-model="formData.pwd">
                            <div class="input-action" @click="showPassword = !showPassword">
                              <span class="action-text">{{ showPassword ? t('login.hidePassword') : t('login.showPassword') }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="button-group">
                        <div class="primary-button" @click="handleLogin" :class="{ 'button-disabled': loading }">
                            <div class="button-content">
                                <span class="button-text">{{ loading ? t('login.logging') : t('login.loginButton') }}</span>
                                <span class="button-icon" v-if="!loading">→</span>
                                <span class="loading-icon" v-else>⏳</span>
                            </div>
                        </div>
                    </div>
                    <div class="button-group">
                        <div class="secondary-button" @click="goToRegister">
                            <div class="button-content"><span class="button-text">{{ t('login.registerButton') }}</span><span class="button-icon">+</span></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

     </div>
  </div>
</template>


<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { login, getLoginServiceData } from '@/api'
import { TOKEN_KEY } from '@/libs/util'
import LanguageDropdown from '@/components/LanguageDropdown.vue'
import '@/assets/css/login.css'

const router = useRouter()
const { t } = useI18n()
const languageDropdownRef = ref(null)

const formData = ref({
  tel: '',
  pwd: ''
})

const loading = ref(false)
const showPassword = ref(false)
const kefuUrl = ref('')

const loadCachedCredentials = () => {
  const cachedTel = localStorage.getItem('cached_tel')
  const cachedPwd = localStorage.getItem('cached_pwd')
  if (cachedTel) {
    formData.value.tel = cachedTel
  }
  if (cachedPwd) {
    formData.value.pwd = cachedPwd
  }
}

const fetchKefuUrl = async () => {
  try {
    const response = await getLoginServiceData()
    if (response.data.code === 200 && response.data.data) {
      kefuUrl.value = response.data.data.url
    }
  } catch (error) {
    console.error('获取客服链接失败:', error)
  }
}

onMounted(() => {
  fetchKefuUrl()
  loadCachedCredentials()
})

const handleLogin = async () => {
  if (loading.value) {
    return
  }
  
  if (!formData.value.tel) {
    ElMessage.warning(t('login.phoneRequired'))
    return
  }
  
  if (!formData.value.pwd) {
    ElMessage.warning(t('login.passwordRequired'))
    return
  }
  
  try {
    loading.value = true
    
    const response = await login({
      tel: formData.value.tel,
      pwd: formData.value.pwd
    })
    
    if (response.data.code === 200) {
      localStorage.setItem(TOKEN_KEY, response.data.data.token)
      localStorage.setItem('cached_tel', formData.value.tel)
      localStorage.setItem('cached_pwd', formData.value.pwd)
      
      ElMessage.success(t('login.loginSuccess'))
      window.location.href = '/'
    } else {
      ElMessage.error(response.data.message || t('login.loginFailed'))
    }
  } catch (error) {
    console.error('登录错误:', error)
    ElMessage.error(t('login.loginError'))
  } finally {
    loading.value = false
  }
}

const goToRegister = () => {
  router.push('/register')
}

const handleKefu = () => {
  if (kefuUrl.value) {
    window.location.href = kefuUrl.value
  }
}
</script>


<style scoped>
.action-btn {
    width: 36px;
    height: 36px;
    background: #fff3;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(5px);
    transition: all .3s ease;
    cursor: pointer;
    border: 2px solid rgba(255,255,255,.3)
}

.action-btn:hover {
    background: #fff6;
    transform: scale(1.1);
    box-shadow: 0 4px 15px #0003
}

.action-btn:active {
    transform: scale(.95);
    background: #ffffff4d
}

.action-btn .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%
}

.action-btn .icon-text {
    font-size: 16px;
    filter: grayscale(0%)
}

.input-action {
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
}

.input-action:hover {
  color: #42b883;
}


.header-section {
    padding: 30px 20px 20px;
    position: relative;
    z-index: 50
}

.header-content {
    position: relative
}

.header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px
}

.app-logo {
    width: 110px;
    height: auto
}

.header-actions {
    display: flex;
    gap: 8px
}

.language-dropdown {
    position: absolute;
    top: 50px;
    right: 0;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 10px 30px #00000026;
    z-index: 100;
    min-width: 160px;
    overflow: hidden;
    animation: dropdownFadeIn-dc434e7e .3s ease
}
</style>
